import React, { ReactNode, useRef, useState } from "react";
import { ModalsKeys } from "global";
import { logError } from "utils/logger";
import { useInvoice } from "modules/invoice";
import { useNotifications } from "modules/notification";
import { useHistory, useParams } from "react-router-dom";
import { capitalize, Stack, Button } from "@mui/material";
import { collectInvoiceDTOFromQuote } from "modules/quote/lib";
import { ListingForm } from "components/shared/TableListing/types";
import { PageContainer } from "components/shared/Page/PageContainer";
import { CardTitle } from "components/shared/CardContainer/CardTitle";
import { CardContainer } from "components/shared/CardContainer/CardContainer";
import { CalculatedTotalsByLevel, QuoteChargeListingEntity } from "modules/quote/types";
import { RenderWithFallback } from "components/shared/Conditional/RenderWithFallback";
import {
  useQuote,
  QuoteListingLoading,
  InvoiceDetailsListing,
  NetFinanceAmountListing,
  collectQuoteFromInvoiceForm,
} from "modules/quote";
import {
  useVOEquipment,
  CreateChargesDialog,
  EquipmentChargesDialog,
  CreateUpdateEquipmentDialog,
} from "modules/equipment";

const modals: Partial<Record<ModalsKeys, ReactNode>> = {
  [ModalsKeys.ChargesEquipmentDialog]: <EquipmentChargesDialog />,
  [ModalsKeys.CreateChargesEquipmentDialog]: <CreateChargesDialog />,
  [ModalsKeys.CreateUpdateEquipmentDialog]: <CreateUpdateEquipmentDialog />,
};

export const QuotePage = () => {
  const chargesSubmitRef = useRef<{ submit: () => ListingForm<QuoteChargeListingEntity> } | null>(null);

  const [calculatedTotalsByLevel, setCalculatedTotalsByLevel] = useState<CalculatedTotalsByLevel>({
    invoice: { fees: 0, taxes: 0, price: 0 },
    equipments: { fees: 0, taxes: 0, price: 0 },
    attachments: { fees: 0, taxes: 0, price: 0 },
  });

  const history = useHistory();
  const { VOId, dynamicsVOId } = useParams<{ VOId: string; dynamicsVOId: string }>();
  const { showNotification } = useNotifications();

  const { handleUpdateInvoice } = useInvoice();
  const { quote, isQuoteLoading, handleCreateOrUpdateQuote, isQuoteCreatingOrUpdating } = useQuote(VOId);
  const { isLoading, equipments } = useVOEquipment(VOId);

  const handleGoToVO = () => {
    history.push(`/vo/${VOId}`);
  };

  const handleSubmitAll = async () => {
    try {
      const valuesCharges = chargesSubmitRef.current?.submit()?.bodyFields ?? [];

      const quoteDTO = collectQuoteFromInvoiceForm(valuesCharges);
      await handleCreateOrUpdateQuote(VOId, quoteDTO);

      const invoiceDTO = collectInvoiceDTOFromQuote(quoteDTO);
      await handleUpdateInvoice({ VOId, entityId: dynamicsVOId, fieldsObj: invoiceDTO });

      showNotification("Successfully Updated!");
    } catch (error) {
      showNotification("Something went wrong", { type: "error" });
      logError(error);
    }
  };

  return (
    <PageContainer modals={modals}>
      <CardContainer
        title={<CardTitle label={`${capitalize(VOId ? "edit" : "add")} Invoice Detail`} onRedirect={handleGoToVO} />}>
        <Stack gap="1rem">
          <RenderWithFallback isLoading={isLoading || isQuoteLoading} fallBack={<QuoteListingLoading />}>
            <InvoiceDetailsListing
              VOId={VOId}
              quote={quote}
              equipmentList={equipments}
              dynamicsVOId={dynamicsVOId}
              chargesSubmitRef={chargesSubmitRef}
              onSubscribeTotals={setCalculatedTotalsByLevel}
            />

            <NetFinanceAmountListing {...calculatedTotalsByLevel} />

            <Stack direction="row" gap="0.5rem" justifyContent="flex-end">
              <Button disabled={isQuoteCreatingOrUpdating} onClick={handleGoToVO}>
                Cancel
              </Button>

              <Button loading={isQuoteCreatingOrUpdating} variant="contained" onClick={handleSubmitAll}>
                Save changes
              </Button>
            </Stack>
          </RenderWithFallback>
        </Stack>
      </CardContainer>
    </PageContainer>
  );
};
